import classNames from 'classnames';
import React from 'react';
import NotificationIcon from './NotificationIcon';
import { Button } from '@paypalcorp/pp-react-buttons';
import { Icon as PPReactIcon } from '@paypalcorp/pp-react-icons';
import { FPTI_EVENT_NAMES, fptiEventAttributes } from '../../app/consumer/fpti';

const BUTTON_STYLES = {
  secondary: { secondary: true },
  branded: { branded: true },
  'primary-inverse': { inverse: true },
  'secondary-inverse': { secondary: true, inverse: true },
};

/*
  returns the correct button classNames for NotificationTile
*/
const getButtonStyle = (buttonStyle, iconTypeToUse) => {
  if (buttonStyle === 'icon') {
    return {
      iconComponent: () => <PPReactIcon name={iconTypeToUse} size="sm" />,
    };
  }
  if (buttonStyle === 'icon-inverse') {
    return {
      inverse: true,
      iconComponent: () => <PPReactIcon name={iconTypeToUse} size="sm" />,
    };
  }
  return BUTTON_STYLES[buttonStyle];
};
export interface NotificationProps {
  id: string;
  entityId: string;
  name: string;
  title: string;
  timestamp: number;
  description: string;
  trackingDetails: { clickUrl: string; impressionUrl: string; presentUrl: string };
  dismissible: boolean;
  iconType: string;
  iconIdentifier: string;
  timestampFormatted: string;
  backgroundColor: string;
  contributeToBadge: boolean;
  actionDetails?: { text?: string; navigation?: { url?: string }; buttonStyle?: string };
  iconDetails?: { color: string; type: string; identifier: string };
}
interface NotificationItemProps {
  sectionTitle: string;
  notification: NotificationProps;
  correlationId: string;
}
/*
	renders a single notification tile that will be present in the notification body
*/
export default function NotificationItem({ sectionTitle, notification, correlationId }: NotificationItemProps) {
  const DEFAULT_BUTTON_STYLE = 'secondary';
  const DEFAULT_BUTTON_SIZE = 'sm';
  const {
    id,
    entityId: cardId,
    name: cardName,
    title,
    description,
    trackingDetails,
    dismissible,
    iconType,
    iconIdentifier,
    timestampFormatted,
    backgroundColor,
    contributeToBadge,
    actionDetails: { text: actionText, navigation: actionNavigation } = {},
    iconDetails: { color: iconDetailsColor = null } = {},
  } = notification;
  const { clickUrl, impressionUrl, presentUrl } = trackingDetails;
  const headerContent = (title || '').trim();

  const linkContent = actionText;
  const background = {
    backgroundColor: backgroundColor,
  };

  const CTA = actionNavigation?.url;
  const buttonStyle = notification?.actionDetails?.buttonStyle || DEFAULT_BUTTON_STYLE;
  const buttonSize = DEFAULT_BUTTON_SIZE;
  const iconTypeToUse = notification?.iconDetails?.type || iconType;
  const iconIdentifierToUser = notification?.iconDetails?.identifier || iconIdentifier;

  const buttonClassNames = classNames('cw_tile-button_new', {
    js_notification_commandClick: actionNavigation,
  });

  const fptiData = {
    domain_type: sectionTitle,
    card_id: cardId,
    card_type: cardName,
  };

  const clickFptiData = {
    pglk: 'main:walletweb:notificationcenter::main|alertClick',
    pgln: 'main:walletweb:notificationcenter::main|alertClick',
    link: `moremenu-${correlationId}`,
  };

  return (
    <li
      key={id}
      id={`notification-${id}`}
      className="cw_notification_new"
      style={background}
      data-cwt-ps-im={impressionUrl ? impressionUrl : ''}
      data-cwt-ps-pr={presentUrl ? presentUrl : ''}
      {...fptiEventAttributes({
        eventName: FPTI_EVENT_NAMES.NOTIFICATIONS_ITEM,
        base: fptiData,
        click: clickFptiData,
        impression: true,
        load: true,
      })}
    >
      <div className="cw_notification-description cw_notification-description_new">
        <NotificationIcon iconType={iconTypeToUse} iconIdentifier={iconIdentifierToUser} iconColor={iconDetailsColor} />
        <span>
          {headerContent && <div className="cw_notifications-header_new">{headerContent}</div>}
          <div className="cw_notifications-descriptionContent_new" dangerouslySetInnerHTML={{ __html: description }} />
          {!CTA || !actionText ? (
            ''
          ) : (
            <div className="cw_notifications__button-wrap">
              <Button
                as="a"
                href={CTA}
                role="button"
                size={buttonSize}
                className={buttonClassNames}
                {...getButtonStyle(buttonStyle, iconTypeToUse)}
                data-id={cardId}
                data-url={CTA}
                data-counttobadge={contributeToBadge}
                data-sectiontitle={sectionTitle}
                data-cardtype={cardName}
                data-dismissible={dismissible}
                data-cwt-ps-cl={clickUrl}
                data-test-id={`notification-btn-${id}`}
              >
                {linkContent}
              </Button>
            </div>
          )}
        </span>
        <span className="cw_notifications-time">{timestampFormatted}</span>
      </div>
    </li>
  );
}
